import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft, ExternalLink, Loader2, Save, Pencil, X,
  Send, FileText, Camera, Facebook, Instagram, Linkedin, Check, Globe, Type,
  Tag, User, ChevronDown, ChevronRight, Sparkles, Upload, CalendarClock,
  Image as ImageIcon, Link2, ShieldCheck, MessageSquare, Pin, PinOff,
  Highlighter, Undo2,
} from "lucide-react";
import { api, BACKEND_BASE } from "@/lib/api";
import HorizontalizeDialog from "@/components/HorizontalizeDialog";
import { isActive as horizontalizeIsActive } from "@/lib/horizontalize";
import {
  appliedMessage, checkedLabel, fixState, locationBadge, pendingFixCount,
  scoreNote, undoneMessage,
  missingDocsLabel, sourceDisplay, sourcesSummary,
  readPinned, writePinned,
} from "@/lib/factCheckView";
import { claimCandidates, splitForHighlight } from "@/lib/claimLocator";
import { COURTESY_PREFIX, sourceNameSuggestions } from "@/lib/courtesyNames";
import { hintDay, scheduleHint, slotsTooltip } from "@/lib/scheduleHint";
import { todayInTz } from "@/lib/calendarDates";
import { clearClaimHighlight, highlightClaim } from "@/lib/claimHighlight";
import SourceTrimNotice from "@/components/SourceTrimNotice";
import { ARTICLE, CATEGORY_PICKER, IMAGES } from "@/constants/testIds";
import {
  formatInTz,
  localInputToUtcIso,
  propertyTimezone,
  tzAbbreviation,
  utcIsoToLocalInput,
} from "@/lib/timezone";
import ReviseArticlePanel from "@/components/ReviseArticlePanel";
import InternalLinksPanel from "@/components/InternalLinksPanel";
import StoryCardPanel from "@/components/StoryCardPanel";
import CarouselPanel from "@/components/CarouselPanel";
import SourcesPanel from "@/components/SourcesPanel";
import RichTextEditor from "@/components/RichTextEditor";
import { ArticleBodyWithImages } from "@/components/ArticleBodyWithImages";
import FacebookSharePanel from "@/components/FacebookSharePanel";
import ScheduledPostsList from "@/components/ScheduledPostsList";
import StoryReviewPanel from "@/components/StoryReviewPanel";
import StockPhotoPicker from "@/components/StockPhotoPicker";
import AddToStockOption, { useAddToStock } from "@/components/AddToStockOption";
import { SourceStockButton, useSourceStock } from "@/components/SourceToStock";
import { ArticleStockButton, useArticleStock } from "@/components/ArticleImageToStock";
import { legendOverlayStyle } from "@/lib/legendStyle";
import PasteImageButton from "@/components/PasteImageButton";
import {
  chipLabel,
  chipTitle,
  historyGroups,
  visibleReposts,
} from "@/lib/socialHistory";

/**
 * One labelled textarea inside the "Excerpt, social copy & SEO" section.
 * Edit-only: these fields are read from the Quick copy bar, not here.
 */
function DerivativeField({ label, icon: Icon, testId, value, onChange, rows }) {
  return (
    <div className="px-5 py-4">
      <div className="flex items-center gap-1.5 mb-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-[var(--accent)] shrink-0" />}
        <span className="overline text-[var(--ink-muted)]">{label}</span>
      </div>
      <textarea
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full text-sm leading-relaxed outline-none resize-y"
      />
    </div>
  );
}

/**
 * Collapsible wrapper for every section below the article body. Header
 * is always visible (icon + title + optional badge + chevron); the
 * body slides in when clicked. Sections default to closed to reduce
 * scroll fatigue — the editor opens what they need. Sections that
 * demand attention (e.g. an in-flight IG publish) can force-open via
 * `defaultOpen`.
 */
function CollapsibleSection({
  title,
  icon: Icon,
  defaultOpen = false,
  badge,
  testId,
  children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={testId} className="mb-3">
      <div
        className={
          "w-full flex items-center gap-2 border bg-white " +
          "transition-colors " +
          (open
            ? "border-[var(--ink)]"
            : "border-[var(--border)]")
        }
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid={testId ? `${testId}-toggle` : undefined}
          className="flex-1 flex items-center gap-2 px-4 py-2.5 hover:bg-neutral-50 min-w-0"
        >
          <ChevronRight
            className={
              "w-3.5 h-3.5 text-[var(--ink-muted)] transition-transform shrink-0 " +
              (open ? "rotate-90" : "")
            }
          />
          {Icon && (
            <Icon className="w-3.5 h-3.5 text-[var(--accent)] shrink-0" />
          )}
          <span className="overline text-left truncate">{title}</span>
        </button>
        {badge && <div className="pr-3 shrink-0">{badge}</div>}
      </div>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

/**
 * Sits under the green WordPress-link bar and lists a chip per social
 * post (Facebook = light blue, Instagram = light pink): the original
 * post per Page, then a "Repost · <date>" chip for every repost, so no
 * post's link is ever lost (lib/socialHistory.js). Also shows the
 * in-flight "publishing to IG…" pill so editors have real-time
 * feedback while the background task runs.
 */
function SocialPostLinks({ article, tz }) {
  const qc = useQueryClient();
  const groups = historyGroups(article);
  const [expanded, setExpanded] = useState({});
  const fmtDate = (iso) => formatInTz(iso, tz, "compact", {
    hour: undefined, minute: undefined, timeZoneName: undefined,
  });
  // Delete = platform first, then every Newsdesk record (article badge,
  // Content Calendar, sponsor placements). When the platform refuses
  // (Instagram has no delete API, a Page was disconnected) the server
  // answers 409 and we offer "remove from Newsdesk only".
  const removeMut = useMutation({
    mutationFn: (vars) =>
      api.deleteSocialPost(article.id, vars),
    onSuccess: (data) => {
      const label = data.platform === "facebook" ? "Facebook" : "Instagram";
      if (data.local_only) {
        toast.success(
          `Removed from Newsdesk. If the post is still live, delete it on ${label} too.`,
        );
      } else if (data.already_gone) {
        toast.success(`Already gone from ${label} — removed from Newsdesk.`);
      } else {
        toast.success(`Post deleted from ${label} and Newsdesk.`);
      }
      qc.invalidateQueries({ queryKey: ["article", article.id] });
      qc.invalidateQueries({ queryKey: ["content-calendar"] });
      qc.invalidateQueries({ queryKey: ["ad-analytics"] });
      qc.invalidateQueries({ queryKey: ["ads"] });
    },
    onError: (e, vars) => {
      const label = vars.platform === "facebook" ? "Facebook" : "Instagram";
      const detail = e?.response?.data?.detail || "Could not delete the post";
      if (e?.response?.status === 409 && !vars.local_only) {
        const ok = window.confirm(
          `${detail}\n\nRemove it from Newsdesk only? The post stays live on ${label} until you delete it there.`,
        );
        if (ok) {
          removeMut.mutate({ ...vars, local_only: true });
          return;
        }
      }
      toast.error(detail);
    },
  });
  const handleRemove = (p) => {
    const isFbPost = p.platform === "facebook";
    const name = isFbPost
      ? p.page_name || "Facebook"
      : p.ig_username || p.page_name || "Instagram";
    const msg = isFbPost
      ? `Delete this Facebook post on ${name}?\n\nIt will be removed from Facebook and from Newsdesk (this article, the Content Calendar and Sponsored Posts). This cannot be undone.`
      : `Remove this Instagram post on ${name}?\n\nNewsdesk will ask Instagram to delete it and remove it from this article, the Content Calendar and Sponsored Posts. Instagram does not let apps delete posts, so if it refuses you can remove it from Newsdesk only and delete it in the Instagram app yourself.`;
    if (!window.confirm(msg)) return;
    removeMut.mutate({
      platform: p.platform,
      post_id: p.post_id,
      local_only: false,
    });
  };
  // Originals keep their old test ids; a Story gets a "-story" suffix
  // and a repost is addressed by its post id.
  const chipTestId = (prefix, p) =>
    p.repost_number
      ? `${prefix}-repost-${p.post_id}`
      : `${prefix}-${p.platform}-${p.page_id}${p.kind === "story" ? "-story" : ""}`;
  const renderChip = (p) => {
    const isFb = p.platform === "facebook";
    const removing =
      removeMut.isPending && removeMut.variables?.post_id === p.post_id;
    return (
      <span
        key={p.post_id || `${p.platform}-${p.page_id}-${p.kind}`}
        data-testid={chipTestId("social-chip", p)}
        className={
          "inline-flex items-stretch border max-w-full " +
          (isFb
            ? "bg-sky-50 border-sky-200 text-sky-800"
            : "bg-rose-50 border-rose-200 text-rose-800")
        }
      >
        {p.post_id && (
          <button
            type="button"
            data-testid={chipTestId("social-delete", p)}
            onClick={() => handleRemove(p)}
            disabled={removeMut.isPending}
            title={
              isFb
                ? "Delete this post from Facebook and Newsdesk"
                : "Delete this post from Instagram and Newsdesk"
            }
            aria-label="Delete post"
            className={
              "px-1.5 flex items-center border-r disabled:opacity-50 " +
              (isFb
                ? "border-sky-200 hover:bg-red-100 hover:text-red-700"
                : "border-rose-200 hover:bg-red-100 hover:text-red-700")
            }
          >
            {removing ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <X className="w-3 h-3" />
            )}
          </button>
        )}
        <a
          href={p.permalink || "#"}
          target="_blank"
          rel="noreferrer"
          data-testid={chipTestId("social-link", p)}
          className={
            "inline-flex items-center gap-1.5 px-2 py-1 truncate min-w-0 " +
            (isFb ? "hover:bg-sky-100" : "hover:bg-rose-100")
          }
          title={chipTitle(p)}
        >
          {isFb ? (
            <Facebook className="w-3.5 h-3.5 shrink-0" />
          ) : (
            <Instagram className="w-3.5 h-3.5 shrink-0" />
          )}
          <span className="truncate">{chipLabel(p, fmtDate)}</span>
          <ExternalLink className="w-3 h-3 shrink-0 opacity-60" />
        </a>
      </span>
    );
  };
  const igStatus = article.ig_publish_status;
  const publishingIg =
    igStatus && igStatus.status === "publishing";
  const igFailed = igStatus && igStatus.status === "failed";
  if (!groups.length && !publishingIg && !igFailed) return null;
  return (
    <div className="px-4 sm:px-10 py-2 border-t border-[var(--border)] bg-white flex flex-wrap items-center gap-2 text-xs">
      {groups.map((g) => {
        const { shown, hidden } = visibleReposts(g, expanded[g.key]);
        return (
          <span key={g.key} className="contents">
            {[g.original, ...shown].map((p) => renderChip(p))}
            {hidden > 0 && (
              <button
                type="button"
                data-testid={`social-reposts-more-${g.original.platform}-${g.original.page_id}`}
                onClick={() => setExpanded((e) => ({ ...e, [g.key]: true }))}
                className={
                  "px-2 py-1 border " +
                  (g.original.platform === "facebook"
                    ? "bg-sky-50 border-sky-200 text-sky-800 hover:bg-sky-100"
                    : "bg-rose-50 border-rose-200 text-rose-800 hover:bg-rose-100")
                }
                title="Show older reposts"
              >
                +{hidden} more
              </button>
            )}
          </span>
        );
      })}
      {publishingIg && (
        <span
          data-testid="ig-publishing-pill"
          className="inline-flex items-center gap-1.5 px-2 py-1 border border-rose-200 bg-rose-50 text-rose-800"
        >
          <Loader2 className="w-3 h-3 animate-spin" />
          Publishing to Instagram…
        </span>
      )}
      {igFailed && (
        <div
          data-testid="ig-failed-block"
          className="inline-flex flex-col gap-1 px-2 py-1 border border-red-300 bg-red-50 text-red-800 max-w-full"
        >
          <div className="inline-flex items-center gap-1.5">
            <X className="w-3 h-3" />
            <span>IG publish failed</span>
            <span className="text-[10px] opacity-70 truncate max-w-[280px]">
              {igStatus?.error || ""}
            </span>
          </div>
          {igStatus?.image_probe && (
            <details className="mt-1 text-[10px]">
              <summary className="cursor-pointer opacity-80 hover:opacity-100">
                Show image URL probe
              </summary>
              <pre className="mt-1 p-1.5 bg-white/60 whitespace-pre-wrap break-all max-w-[520px]">
                {JSON.stringify(
                  {
                    image_url: igStatus.image_url,
                    probe: igStatus.image_probe,
                    meta_raw: igStatus.meta_raw,
                  },
                  null,
                  2,
                )}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function stripHtml(html) {
  if (!html) return "";
  const el = document.createElement("div");
  el.innerHTML = html;
  return (el.textContent || el.innerText || "").replace(/\n{3,}/g, "\n\n").trim();
}

function wpHostFromUrl(url) {
  if (!url) return "";
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return String(url).replace(/^https?:\/\//, "").replace(/\/$/, "");
  }
}

function QuickCopyIcon({ icon: Icon, label, text, testId, disabled }) {
  const [copied, setCopied] = useState(false);
  const canCopy = !disabled && !!text;
  return (
    <button
      data-testid={testId}
      disabled={!canCopy}
      onClick={async () => {
        if (!canCopy) return;
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          toast.success(`${label} copied`);
          setTimeout(() => setCopied(false), 1400);
        } catch {
          toast.error("Copy failed");
        }
      }}
      title={disabled ? `${label} — not available` : `Copy ${label.toLowerCase()}`}
      className={
        "group inline-flex items-center justify-center w-9 h-9 border transition-colors " +
        (canCopy
          ? "border-[var(--border)] bg-white hover:border-[var(--ink)] hover:bg-[var(--secondary)]"
          : "border-[var(--border)] bg-[var(--bg)] opacity-40 cursor-not-allowed")
      }
      aria-label={`Copy ${label}`}
    >
      {copied ? (
        <Check className="w-4 h-4 text-emerald-700" />
      ) : (
        <Icon className="w-4 h-4 text-[var(--ink)]" />
      )}
    </button>
  );
}

function QuickCopyBar({ article }) {
  const fullArticle = [
    (article.headline || "").trim(),
    (article.dek || "").trim(),
    stripHtml(article.body_html),
  ]
    .filter(Boolean)
    .join("\n\n");

  return (
    <div
      data-testid="quick-copy-bar"
      className="mb-6 flex items-center gap-2 flex-wrap"
    >
      <span className="overline text-[var(--ink-muted)] mr-1">Quick copy</span>
      <QuickCopyIcon
        icon={Facebook}
        label="Facebook post"
        text={article.facebook_post}
        testId="quick-copy-facebook"
      />
      <QuickCopyIcon
        icon={Instagram}
        label="Instagram post"
        text={article.instagram_post}
        testId="quick-copy-instagram"
      />
      <QuickCopyIcon
        icon={Linkedin}
        label="LinkedIn post"
        text={article.linkedin_post}
        testId="quick-copy-linkedin"
      />
      <QuickCopyIcon
        icon={FileText}
        label="Full article"
        text={fullArticle}
        testId="quick-copy-article"
      />
      <QuickCopyIcon
        icon={Type}
        label="Excerpt"
        text={article.excerpt}
        testId="quick-copy-excerpt"
      />
      <QuickCopyIcon
        icon={Globe}
        label="Meta description"
        text={article.meta_description}
        testId="quick-copy-meta"
      />
    </div>
  );
}

export default function ArticlePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: article, isLoading } = useQuery({
    queryKey: ["article", id],
    queryFn: () => api.getArticle(id),
  });

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
  });

  // Reviewer comments from public review links. Same query key as
  // StoryReviewPanel, so react-query shares one request between the
  // section badge and the panel.
  const { data: reviewData } = useQuery({
    queryKey: ["review-comments", id],
    queryFn: () => api.listReviewComments(id),
    refetchInterval: 60000,
  });
  const reviewOpenCount = reviewData?.open_count || 0;

  const [isEditing, setIsEditing] = useState(false);
  const [catPickerOpen, setCatPickerOpen] = useState(false);
  // Fact check pinned to a side panel (md+ only — on phones the
  // aside is hidden and the section stays under the article). The
  // choice is remembered per browser; see lib/factCheckView.js.
  const [factPinned, setFactPinned] = useState(() => readPinned());
  const toggleFactPinned = () => {
    setFactPinned((v) => {
      writePinned(!v);
      return !v;
    });
  };
  const [editForm, setEditForm] = useState({
    headline: "", dek: "", body_html: "", excerpt: "", meta_description: "",
    facebook_post: "", instagram_post: "", linkedin_post: "",
  });

  useEffect(() => {
    if (article) {
      setEditForm({
        headline: article.headline || "",
        dek: article.dek || "",
        body_html: article.body_html || "",
        excerpt: article.excerpt || "",
        meta_description: article.meta_description || "",
        facebook_post: article.facebook_post || "",
        instagram_post: article.instagram_post || "",
        linkedin_post: article.linkedin_post || "",
      });
    }
  }, [article]);

  const saveMutation = useMutation({
    mutationFn: (patch) => api.updateArticle(id, patch),
    onSuccess: () => {
      toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["article", id] });
      setIsEditing(false);
    },
    onError: () => toast.error("Save failed"),
  });

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");

  const publishMutation = useMutation({
    mutationFn: ({ status, scheduledDate }) =>
      api.publishArticle(id, status, scheduledDate),
    onSuccess: (data) => {
      // Distinguish "brand new publish" from "update to an existing
      // WP post" so the editor's toast reflects what actually happened.
      const wasUpdate = !!article?.wordpress_post_id;
      let kind = "Sent as draft";
      if (data.status === "published_live") {
        kind = wasUpdate ? "Updated live on" : "Published live to";
      } else if (data.status === "scheduled") {
        kind = wasUpdate ? "Schedule updated on" : "Scheduled to";
      } else {
        kind = wasUpdate ? "Draft updated on" : "Sent as draft to";
      }
      toast.success(`${kind} WordPress`);
      setScheduleOpen(false);
      setScheduleAt("");
      qc.invalidateQueries({ queryKey: ["article", id] });
      // Keeps the Schedule panel's "last article" hint and the
      // Content Calendar current.
      qc.invalidateQueries({ queryKey: ["content-calendar"] });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "WordPress publish failed"),
  });

  // "Last article for today scheduled for 4:32 PM" under the Schedule
  // date picker (lib/scheduleHint.js). Follows the picked date — today
  // until one is chosen. Same data and query key shape as the Content
  // Calendar, fetched only while the panel is open. The key holds the
  // DAY, not the full date-time, so changing the time doesn't refetch.
  const scheduleTz = propertyTimezone(settings);
  const scheduleDay = hintDay(scheduleAt, scheduleTz);
  const dayCalendarQuery = useQuery({
    queryKey: ["content-calendar", scheduleDay, scheduleDay],
    queryFn: () => api.getContentCalendar(scheduleDay, scheduleDay),
    enabled: scheduleOpen,
    staleTime: 30000,
  });
  const dayHint = dayCalendarQuery.data
    ? scheduleHint(dayCalendarQuery.data.items, {
        day: scheduleDay,
        today: todayInTz(scheduleTz),
        articleId: id,
      })
    : null;

  const factCheckMutation = useMutation({
    mutationFn: () => api.runArticleFactCheck(id),
    onSuccess: (data) => {
      const latest = (data?.fact_checks || []).slice(-1)[0];
      const score = latest?.score;
      const flagged = latest?.flagged_claims?.length || 0;
      toast.success(
        `Fact check complete · Score ${score}/100 · ${flagged} flagged claim${
          flagged === 1 ? "" : "s"
        }`,
      );
      qc.invalidateQueries({ queryKey: ["article", id] });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Fact check failed"),
  });

  if (isLoading || !article) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[var(--ink-muted)]" />
      </div>
    );
  }

  // Digital editions have their own page (the article editor would
  // mangle the embedded viewer). Links from the Content Calendar,
  // Scheduled tasks etc. land here, so send them on.
  if (article.kind === "edition") {
    return <Navigate to={`/editions/${article.id}`} replace />;
  }

  const isDraftPublished = article.status === "published_draft";
  const isLivePublished = article.status === "published_live";

  return (
    <div className="flex flex-col h-full">
      {/* Sticky header */}
      <div className="border-b border-[var(--border)] bg-white sticky top-0 z-10">
        <div className="px-4 sm:px-10 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              data-testid={ARTICLE.backButton}
              onClick={() => navigate("/library")}
              className="p-1.5 -ml-1.5 hover:bg-[var(--secondary)] shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <div className="overline">{article.writer_name}</div>
              <div className="text-xs text-[var(--ink-muted)] font-mono-display truncate">
                {formatInTz(article.created_at, propertyTimezone(settings))} ·{" "}
                {isLivePublished
                  ? `Live on ${wpHostFromUrl(settings?.wp_url) || "WordPress"}`
                  : isDraftPublished
                    ? "In WP drafts"
                    : "Not yet published"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
            <button
              data-testid="fact-check-btn"
              data-ai="true"
              onClick={() => factCheckMutation.mutate()}
              disabled={factCheckMutation.isPending || isEditing}
              title={
                article.fact_checks?.length
                  ? `Run another fact check. Latest score: ${
                      article.fact_checks[article.fact_checks.length - 1].score
                    }/100. Re-running keeps history.`
                  : "Cross-check every claim in the article against its sources with an LLM. Returns a 0-100 score and a bullet list of flagged claims."
              }
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white disabled:opacity-60"
            >
              {factCheckMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <ShieldCheck className="w-3.5 h-3.5" />
              )}
              <span className="hidden sm:inline">
                {article.fact_checks?.length ? "Re-check facts" : "Fact check"}
              </span>
              <span className="sm:hidden">Facts</span>
              {article.fact_checks?.length ? (
                <span
                  data-testid="fact-check-latest-score-badge"
                  className="ml-1 text-[10px] font-mono-display px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300"
                >
                  {
                    article.fact_checks[article.fact_checks.length - 1].score
                  }
                </span>
              ) : null}
            </button>
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:bg-[var(--secondary)]"
                >
                  <X className="w-3.5 h-3.5" />
                  Cancel
                </button>
                <button
                  data-testid={ARTICLE.saveButton}
                  onClick={() => saveMutation.mutate(editForm)}
                  disabled={saveMutation.isPending}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--slate-900)] text-white hover:bg-black disabled:opacity-60"
                >
                  {saveMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Save
                </button>
              </>
            ) : (
              <button
                data-testid={ARTICLE.editButton}
                onClick={() => setIsEditing(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:bg-[var(--secondary)]"
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit
              </button>
            )}

            <button
              data-testid={ARTICLE.publishDraftButton}
              onClick={() => publishMutation.mutate({ status: "draft" })}
              disabled={publishMutation.isPending || isEditing}
              title={
                article.wordpress_post_id
                  ? "Revert the existing WordPress post to draft with the current edits — permalink preserved."
                  : "Send this article to WordPress as a draft."
              }
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--slate-900)] text-[var(--slate-900)] hover:bg-[var(--slate-900)] hover:text-white disabled:opacity-60"
            >
              {publishMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">
                {article.wordpress_post_id ? "Revert to draft" : "Send as draft"}
              </span>
              <span className="sm:hidden">
                {article.wordpress_post_id ? "Draft" : "Draft"}
              </span>
            </button>
            <button
              data-testid="publish-schedule-btn"
              onClick={() => setScheduleOpen((v) => !v)}
              disabled={publishMutation.isPending || isEditing}
              title={
                article.wordpress_post_id
                  ? "Reschedule the existing WordPress post — permalink is preserved."
                  : "Schedule this article to publish at a future date. Permalink is generated up-front so downstream shares work correctly."
              }
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-blue-600 text-blue-700 hover:bg-blue-600 hover:text-white disabled:opacity-60"
            >
              <CalendarClock className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">
                {article.wordpress_post_id ? "Reschedule" : "Schedule"}
              </span>
              <span className="sm:hidden">
                {article.wordpress_post_id ? "Reschedule" : "Schedule"}
              </span>
            </button>
            <button
              data-testid={ARTICLE.publishLiveButton}
              onClick={() => publishMutation.mutate({ status: "publish" })}
              disabled={publishMutation.isPending || isEditing}
              title={
                article.wordpress_post_id
                  ? "Push the current edits to the existing WordPress post — the permalink is preserved, no duplicate is created."
                  : "Publish this article live to WordPress."
              }
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-60"
            >
              {publishMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {article.wordpress_post_id ? (
                <>
                  <span className="hidden sm:inline">Update on WordPress</span>
                  <span className="sm:hidden">Update</span>
                </>
              ) : (
                <>
                  <span className="hidden sm:inline">Publish live</span>
                  <span className="sm:hidden">Publish</span>
                </>
              )}
            </button>
          </div>
        </div>
        {scheduleOpen && (
          <div
            data-testid="publish-schedule-panel"
            className="px-4 sm:px-10 py-3 bg-blue-50 border-t border-blue-200 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap"
          >
            <div className="flex items-center gap-2 text-sm text-blue-900">
              <CalendarClock className="w-4 h-4 text-blue-700 shrink-0" />
              <span>Schedule this article to auto-publish in WordPress:</span>
            </div>
            <div className="flex flex-col gap-0.5">
            <input
              data-testid="publish-schedule-input"
              type="datetime-local"
              // `scheduleAt` is a wall-clock string ("YYYY-MM-DDTHH:mm")
              // interpreted as the property's newsroom timezone. The
              // input renders it directly; only `min` needs to be
              // converted from an ISO UTC (Date.now() + 5min) into
              // wall-clock-in-tz so the editor can't pick a slot the
              // scheduler would reject.
              value={scheduleAt}
              min={utcIsoToLocalInput(
                new Date(Date.now() + 5 * 60000).toISOString(),
                propertyTimezone(settings),
              )}
              onChange={(e) => setScheduleAt(e.target.value)}
              className="border border-[var(--border)] bg-white px-2 py-1.5 text-sm focus:outline-none focus:border-[var(--slate-900)]"
            />
            {dayCalendarQuery.isLoading && (
              <span className="text-[11px] text-blue-900/70">
                Checking that day's schedule…
              </span>
            )}
            {dayHint && (
              <span
                data-testid="publish-schedule-last"
                title={slotsTooltip(dayHint.slots)}
                className="text-[11px] text-blue-900/70 leading-snug"
              >
                {dayHint.text}
                {dayHint.detail && (
                  <>
                    <br />
                    {dayHint.detail}
                  </>
                )}
              </span>
            )}
            </div>
            <span className="text-[11px] font-mono-display text-blue-900/70">
              {propertyTimezone(settings)} · {tzAbbreviation(propertyTimezone(settings))}
            </span>
            <button
              data-testid="publish-schedule-confirm"
              onClick={() => {
                if (!scheduleAt) {
                  toast.error("Pick a date and time first");
                  return;
                }
                // WP interprets this wall-clock in its own site tz.
                // We send the wall-clock the editor picked in the
                // newsroom tz — for the standard setup (WP site tz ==
                // newsroom tz) that's exactly the moment they mean.
                publishMutation.mutate({
                  status: "schedule",
                  scheduledDate: `${scheduleAt}:00`,
                });
              }}
              disabled={publishMutation.isPending || !scheduleAt}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {publishMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <CalendarClock className="w-3.5 h-3.5" />
              )}
              Schedule in WordPress
            </button>
            <button
              onClick={() => {
                setScheduleOpen(false);
                setScheduleAt("");
              }}
              className="text-xs text-[var(--ink-muted)] hover:text-[var(--ink)] sm:ml-2"
            >
              Cancel
            </button>
          </div>
        )}
        {article.wordpress_link && (
          <div
            data-testid="wp-status-banner"
            className={
              "px-4 sm:px-10 py-2 border-t text-xs flex items-center gap-2 flex-wrap overflow-hidden " +
              (article.status === "scheduled"
                ? "bg-amber-50 border-amber-200"
                : "bg-emerald-50 border-emerald-200")
            }
          >
            {article.status === "scheduled" ? (
              <>
                <CalendarClock className="w-3.5 h-3.5 text-amber-800 shrink-0" />
                <span
                  data-testid="wp-status-badge"
                  className="uppercase tracking-wider font-mono-display text-[10px] font-semibold text-amber-900"
                >
                  Scheduled
                </span>
                {article.wordpress_scheduled_date && (
                  <span
                    data-testid="wp-scheduled-when"
                    className="text-amber-900 font-mono-display"
                    title={article.wordpress_scheduled_date}
                  >
                    {new Date(
                      article.wordpress_scheduled_date,
                    ).toLocaleString(undefined, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </span>
                )}
                <span className="text-amber-900">·</span>
              </>
            ) : (
              <Globe className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
            )}
            <a
              href={article.wordpress_link}
              target="_blank"
              rel="noreferrer"
              className={
                "hover:underline flex items-center gap-1 truncate min-w-0 " +
                (article.status === "scheduled"
                  ? "text-amber-900"
                  : "text-emerald-800")
              }
            >
              <span className="truncate">{article.wordpress_link}</span>
              <ExternalLink className="w-3 h-3 shrink-0" />
            </a>
          </div>
        )}
        <SocialPostLinks article={article} tz={propertyTimezone(settings)} />
      </div>

      {/* Two independently scrolling columns when the fact check is
          pinned: the article (left) and the fact-check aside (right).
          Scrolling one never scrolls the other. */}
      <div className="flex-1 min-h-0 flex">
      <div className="flex-1 min-w-0 overflow-auto">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
          {/* Warns when source material was trimmed or dropped before
              it reached the writer (article.source_report). */}
          <SourceTrimNotice report={article.source_report} />

          {/* Quick-copy icon bar */}
          <QuickCopyBar article={article} />

          {/* Article body */}
          <div className="mb-16">
            <div className="overline mb-3">
              {article.writer_name}
              {settings?.name ? ` · ${settings.name}` : ""}
            </div>

            {isEditing ? (
              <>
                <input
                  data-testid={ARTICLE.headline}
                  value={editForm.headline}
                  onChange={(e) => setEditForm({ ...editForm, headline: e.target.value })}
                  className="font-serif-display text-4xl leading-tight tracking-tight w-full mb-4 outline-none border-b-2 border-[var(--accent)] pb-2 bg-transparent"
                />
                <input
                  value={editForm.dek}
                  onChange={(e) => setEditForm({ ...editForm, dek: e.target.value })}
                  placeholder="Subhead / deck"
                  className="text-lg text-[var(--ink-muted)] font-serif-display italic w-full mb-6 outline-none border-b border-[var(--border)] pb-1 bg-transparent"
                />
                {/* Search scope for "Show in article" (lib/claimHighlight). */}
                <div data-claim-scope="article">
                <RichTextEditor
                  testId={ARTICLE.body}
                  value={editForm.body_html}
                  onChange={(html) =>
                    setEditForm({ ...editForm, body_html: html })
                  }
                  minHeight={520}
                  placeholder="Article body — paste from Docs/Word or type directly. Formatting is preserved."
                  sources={
                    (article.input_sources && article.input_sources.length > 0)
                      ? article.input_sources
                      : (article.sources_used || []).map((u) => ({
                          url: u,
                          outlet: "",
                          title: "",
                        }))
                  }
                />
                </div>
              </>
            ) : (
              // Search scope for "Show in article" (lib/claimHighlight):
              // headline, dek and body.
              <div data-claim-scope="article">
                <h1
                  data-testid={ARTICLE.headline}
                  className="font-serif-display text-3xl sm:text-5xl leading-tight sm:leading-none tracking-tight mb-4"
                >
                  {article.headline}
                </h1>
                {article.dek && (
                  <p className="font-serif-display italic text-lg sm:text-xl text-[var(--ink-muted)] leading-snug mb-6 sm:mb-8">
                    {article.dek}
                  </p>
                )}
                <div className="hairline-strong mb-8" />
                <ArticleBodyWithImages
                  article={article}
                  canEdit={!article.published_at}
                  courtesyNames={sourceNameSuggestions(article, settings?.sources)}
                />
              </div>
            )}
          </div>

          {/* Revise with AI + version history */}
          <CollapsibleSection
            title="Revise with AI"
            icon={Sparkles}
            testId="section-revise"
          >
            <ReviseArticlePanel article={article} disabled={isEditing} />
          </CollapsibleSection>

          {/* Internal links (SEO) — used + manual archive search */}
          <CollapsibleSection
            title="Internal links (SEO)"
            icon={Link2}
            testId="section-internal-links"
          >
            <InternalLinksPanel article={article} />
          </CollapsibleSection>

          {/* Publishing options: WP author + categories (moved below the article) */}
          <CollapsibleSection
            title="Publishing options · Author, categories, schedule"
            icon={Tag}
            testId="section-publishing"
          >
            <PublishingCard
              article={article}
              settings={settings}
              catPickerOpen={catPickerOpen}
              setCatPickerOpen={setCatPickerOpen}
            />
          </CollapsibleSection>

          {/* Pre-publication review: private link + reviewer comments.
              Re-keyed so the section pops open once open comments load. */}
          <CollapsibleSection
            key={reviewOpenCount > 0 ? "review-open" : "review"}
            title="Review link & comments"
            icon={MessageSquare}
            testId="section-story-review"
            defaultOpen={reviewOpenCount > 0}
            badge={
              reviewOpenCount > 0 ? (
                <span
                  data-testid="story-review-open-badge"
                  className="text-[10px] font-mono-display px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300"
                >
                  {reviewOpenCount} open
                </span>
              ) : null
            }
          >
            <StoryReviewPanel article={article} />
          </CollapsibleSection>

          {/* Article images: source photos + AI-generated / uploaded (moved below the article) */}
          <CollapsibleSection
            title="Article images"
            icon={ImageIcon}
            testId="section-images"
          >
            <ArticleImages article={article} settings={settings} />
          </CollapsibleSection>

          {/* Instagram / Facebook story card */}
          <CollapsibleSection
            title="Story card · Instagram / Facebook"
            icon={ImageIcon}
            testId="section-story-card"
          >
            <StoryCardPanel article={article} settings={settings} />
          </CollapsibleSection>

          {/* Instagram carousel: AI-drafted slides in one story-card
              style, linked by a continuous line, posted as one
              CAROUSEL media (routes/carousel.py). */}
          <CollapsibleSection
            title="Instagram carousel"
            icon={Instagram}
            testId="section-carousel"
          >
            <CarouselPanel article={article} settings={settings} />
          </CollapsibleSection>

          {/* Direct-publish to a connected Facebook Page (link mode or
              photo mode). Instagram publishing lives inside StoryCardPanel
              because it needs the rendered card. */}
          <CollapsibleSection
            title="Publish to Facebook"
            icon={Facebook}
            testId="section-fb-share"
          >
            <FacebookSharePanel article={article} />
          </CollapsibleSection>

          {/* Queued Facebook + Instagram posts for THIS article. Hides
              itself when there's nothing scheduled. */}
          <CollapsibleSection
            title="Scheduled social posts"
            icon={CalendarClock}
            testId="section-scheduled-posts"
          >
            <ScheduledPostsList articleId={article.id} />
          </CollapsibleSection>

          {/* Excerpt, social copy and SEO description.
              These five fields used to have an accordion each, all of
              them collapsed and read-only 99% of the time — the Quick
              copy bar at the top of the page covers reading them. What
              the accordions still did was host the ONLY editor for these
              fields, so they collapse into this one section, which
              appears only while editing. */}
          {isEditing && (
            <CollapsibleSection
              title="Excerpt, social copy & SEO"
              icon={FileText}
              testId="section-derivative-copy"
              defaultOpen
            >
              <div className="bg-white border border-[var(--border)] divide-y divide-[var(--border)]">
                <DerivativeField
                  label="WordPress excerpt"
                  icon={FileText}
                  testId={ARTICLE.excerpt}
                  value={editForm.excerpt}
                  onChange={(v) => setEditForm({ ...editForm, excerpt: v })}
                  rows={3}
                />
                <DerivativeField
                  label="Meta description (SEO)"
                  icon={Globe}
                  testId={ARTICLE.metaDescription}
                  value={editForm.meta_description}
                  onChange={(v) => setEditForm({ ...editForm, meta_description: v })}
                  rows={3}
                />
                <DerivativeField
                  label="Facebook post copy"
                  icon={Facebook}
                  testId={ARTICLE.facebookPost}
                  value={editForm.facebook_post}
                  onChange={(v) => setEditForm({ ...editForm, facebook_post: v })}
                  rows={5}
                />
                <DerivativeField
                  label="Instagram post copy"
                  icon={Instagram}
                  testId={ARTICLE.instagramPost}
                  value={editForm.instagram_post}
                  onChange={(v) => setEditForm({ ...editForm, instagram_post: v })}
                  rows={5}
                />
                <DerivativeField
                  label="LinkedIn post copy"
                  icon={Linkedin}
                  testId="article-linkedin-post"
                  value={editForm.linkedin_post}
                  onChange={(v) => setEditForm({ ...editForm, linkedin_post: v })}
                  rows={7}
                />
              </div>
            </CollapsibleSection>
          )}

          {/* Photo suggestions */}
          <CollapsibleSection
            title="Photo recommendations"
            icon={Camera}
            testId="section-photo-recs"
          >
            <div className="bg-white border border-[var(--border)] px-5 py-4 mb-3">
              <StockPhotoPicker article={article} />
            </div>
            {/* Original outbound search links, kept as a compact fallback
                (Adobe Stock in particular, until its API is wired up). */}
            <div className="grid grid-cols-1 gap-2">
              {(article.photo_suggestions || []).map((p, i) => (
                <div
                  key={i}
                  data-testid={`${ARTICLE.photoSuggestion}-${i}`}
                  className="flex flex-wrap items-center gap-2 px-1 text-xs"
                >
                  <span className="text-[var(--ink-muted)] mr-1">{p.concept}:</span>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {p.adobe_stock_url && (
                      <a
                        href={p.adobe_stock_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 px-2.5 py-1 border border-[var(--border)] hover:border-[var(--ink)]"
                      >
                        Adobe Stock <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {p.envato_url && (
                      <a
                        href={p.envato_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 px-2.5 py-1 border border-[var(--border)] hover:border-[var(--ink)]"
                      >
                        Envato Elements <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {p.unsplash_url && (
                      <a
                        href={p.unsplash_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 px-2.5 py-1 border border-[var(--border)] hover:border-[var(--ink)]"
                      >
                        Unsplash <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>

          {/* Sources */}
          {(article.input_sources?.length > 0 ||
            article.sources_used?.length > 0) && (
            <CollapsibleSection
              title={`Sources consulted by the writer · ${
                (article.input_sources?.length ||
                  article.sources_used?.length) || 0
              }`}
              icon={Globe}
              testId="section-sources"
            >
              <SourcesPanel article={article} />
            </CollapsibleSection>
          )}

          {/* LLM fact-check history — every run is appended (unlimited
              history), most recent first. Section auto-opens on first
              run so the editor sees the result without another click. */}
          {article.fact_checks?.length > 0 && (
            // While pinned, the inline section is hidden from md up
            // (the aside shows it) but stays on phones, where there's
            // no aside.
            <div className={factPinned ? "md:hidden" : ""}>
            <CollapsibleSection
              title={`Fact check history · ${article.fact_checks.length}`}
              icon={ShieldCheck}
              testId="section-fact-checks"
              defaultOpen
              badge={
                <div className="flex items-center gap-2">
                  <span
                    data-testid="fact-check-history-latest-score"
                    className="text-[10px] font-mono-display px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300"
                  >
                    Latest{" "}
                    {
                      article.fact_checks[article.fact_checks.length - 1].score
                    }
                    /100
                  </span>
                  <button
                    type="button"
                    data-testid="fact-check-pin-btn"
                    onClick={toggleFactPinned}
                    title="Pin the fact check to the side so you can read the claims while you work on the article"
                    className="hidden md:inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 border border-[var(--border)] text-[var(--ink-muted)] hover:border-[var(--ink)] hover:text-[var(--ink)]"
                  >
                    <Pin className="w-3 h-3" />
                    Pin to side
                  </button>
                </div>
              }
            >
              <FactCheckHistoryPanel
                article={article}
                settings={settings}
                isEditing={isEditing}
              />
            </CollapsibleSection>
            </div>
          )}
        </div>
      </div>
      {factPinned && article.fact_checks?.length > 0 && (
        <aside
          data-testid="fact-check-sidebar"
          className="hidden md:flex flex-col w-[340px] lg:w-[400px] xl:w-[440px] shrink-0 min-h-0 border-l border-[var(--border)] bg-[var(--bg)]"
        >
          <div className="shrink-0 px-4 py-2.5 border-b border-[var(--border)] bg-white flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5 text-[var(--accent)] shrink-0" />
            <span className="overline flex-1 min-w-0 truncate">
              Fact check · {article.fact_checks.length}
            </span>
            {pendingFixCount(article.fact_checks[article.fact_checks.length - 1]) > 0 && (
              <span
                data-testid="fact-check-sidebar-pending"
                title="Proposed changes in the latest run still waiting for you"
                className="text-[10px] font-mono-display px-1.5 py-0.5 bg-sky-50 text-sky-800 border border-sky-300 shrink-0"
              >
                {pendingFixCount(article.fact_checks[article.fact_checks.length - 1])} to review
              </span>
            )}
            <span className="text-[10px] font-mono-display px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 shrink-0">
              Latest {article.fact_checks[article.fact_checks.length - 1].score}/100
            </span>
            <button
              type="button"
              data-testid="fact-check-unpin-btn"
              onClick={toggleFactPinned}
              title="Unpin — move the fact check back under the article"
              aria-label="Unpin fact check"
              className="p-1 text-[var(--ink-muted)] hover:text-[var(--ink)] hover:bg-[var(--secondary)] shrink-0"
            >
              <PinOff className="w-3.5 h-3.5" />
            </button>
          </div>
          {/* Own scroll; overscroll-contain stops a scroll that hits
              the end of the panel from carrying on into the article. */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3">
            <FactCheckHistoryPanel
              article={article}
              settings={settings}
              isEditing={isEditing}
            />
          </div>
        </aside>
      )}
      </div>
    </div>
  );
}

function FactCheckHistoryPanel({ article, settings, isEditing = false }) {
  const qc = useQueryClient();
  const tz = propertyTimezone(settings);
  // Accept a proposed fix (backend/fact_check_fixes.py). Blocked while
  // the article is in Edit mode: the edit form re-seeds from the
  // article on every refetch, so a fix applied mid-edit would wipe the
  // editor's unsaved changes (or be overwritten by their Save).
  const [applyingKey, setApplyingKey] = useState(null);
  // "Show in article": which claim is highlighted in the article body
  // (key `${checkId}:${index}`), and which social claim has its post
  // preview open. See lib/claimHighlight.js.
  const [shownKey, setShownKey] = useState(null);
  const [previewKey, setPreviewKey] = useState(null);
  // The body DOM changes when a fix is accepted or the article is
  // edited, which leaves the old highlight pointing at stale text.
  useEffect(() => {
    clearClaimHighlight();
    setShownKey(null);
  }, [article.body_html, isEditing]);
  useEffect(() => () => clearClaimHighlight(), []);
  // Esc clears the highlight.
  useEffect(() => {
    if (!shownKey) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        clearClaimHighlight();
        setShownKey(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [shownKey]);
  const toggleShow = (key, claim) => {
    const loc = claim.location || "article";
    if (loc !== "article") {
      setPreviewKey((cur) => (cur === key ? null : key));
      return;
    }
    if (shownKey === key) {
      clearClaimHighlight();
      setShownKey(null);
      return;
    }
    const n = highlightClaim(claimCandidates(claim));
    if (n === 0) {
      setShownKey(null);
      toast.message(
        "Couldn't find this exact wording in the article — the checker may have paraphrased it",
      );
      return;
    }
    setShownKey(key);
    if (n > 1) toast.message(`Highlighted ${n} places in the article`);
  };
  const applyMut = useMutation({
    mutationFn: ({ checkId, index }) =>
      api.applyFactCheckFix(article.id, checkId, index),
    onMutate: ({ checkId, index }) => setApplyingKey(`${checkId}:${index}`),
    onSuccess: (data, { claim }) => {
      qc.setQueryData(["article", article.id], data);
      qc.invalidateQueries({ queryKey: ["article", article.id] });
      toast.success(appliedMessage(claim, data), { duration: 6000 });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Couldn't apply the fix"),
    onSettled: () => setApplyingKey(null),
  });
  // Undo an accepted fix (2026-10-12) — puts the original wording
  // back and brings the Accept button back. Same Edit-mode block and
  // the same busy key as Accept, so the two can't race.
  const undoMut = useMutation({
    mutationFn: ({ checkId, index }) =>
      api.undoFactCheckFix(article.id, checkId, index),
    onMutate: ({ checkId, index }) => setApplyingKey(`${checkId}:${index}`),
    onSuccess: (data, { claim }) => {
      qc.setQueryData(["article", article.id], data);
      qc.invalidateQueries({ queryKey: ["article", article.id] });
      toast.success(undoneMessage(claim, data), { duration: 6000 });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Couldn't undo the change", {
        duration: 8000,
      }),
    onSettled: () => setApplyingKey(null),
  });
  // Newest first — the DB stores oldest → newest for append semantics.
  const history = [...(article.fact_checks || [])].reverse();
  const scoreColor = (s) => {
    if (s >= 85) return "text-emerald-700 border-emerald-300 bg-emerald-50";
    if (s >= 60) return "text-amber-700 border-amber-300 bg-amber-50";
    return "text-red-700 border-red-300 bg-red-50";
  };
  const verdictColor = (v) => {
    const k = (v || "").toLowerCase();
    if (k === "supported") return "bg-emerald-100 text-emerald-800 border-emerald-300";
    if (k === "partial") return "bg-amber-100 text-amber-800 border-amber-300";
    if (k === "contradicted") return "bg-red-100 text-red-800 border-red-300";
    // unsupported / unknown
    return "bg-neutral-100 text-neutral-800 border-neutral-300";
  };
  // Where the checker got its evidence. Entries from before this
  // shipped have no `basis`, so the badge is simply omitted.
  const BASIS_LABEL = {
    source: "from sources",
    web: "from web",
    calendar: "from calendar",
    knowledge: "general knowledge",
  };
  return (
    <div className="space-y-4" data-testid="fact-check-history-panel">
      {history.map((entry, idx) => (
        <div
          key={entry.id || idx}
          data-testid={`fact-check-entry-${idx}`}
          className="bg-white border border-[var(--border)]"
        >
          <div className="px-4 py-2.5 border-b border-[var(--border)] flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                data-testid={`fact-check-score-${idx}`}
                className={
                  "text-sm font-bold font-mono-display px-2 py-1 border " +
                  scoreColor(entry.score)
                }
                title={scoreNote(entry) || undefined}
              >
                {entry.score}/100
              </span>
              {entry.score_estimated && (
                <span
                  data-testid={`fact-check-score-estimated-${idx}`}
                  title={scoreNote(entry)}
                  className="text-[10px] font-mono-display uppercase tracking-wider px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-300"
                >
                  estimated
                </span>
              )}
              <span className="overline">
                Run #{article.fact_checks.length - idx}
              </span>
              {idx === 0 && (
                <span className="text-[10px] font-mono-display px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300">
                  LATEST
                </span>
              )}
              <span className="text-[11px] text-[var(--ink-muted)] font-mono-display">
                {formatInTz(entry.created_at, tz)}
              </span>
              {entry.model && (
                <span
                  className="text-[10px] font-mono-display text-[var(--ink-muted)]"
                  title={`${entry.provider || ""} · ${entry.model}`}
                >
                  {entry.model}
                </span>
              )}
              {entry.reference_date && (
                <span
                  data-testid={`fact-check-reference-date-${idx}`}
                  className="text-[10px] font-mono-display px-1.5 py-0.5 bg-sky-50 text-sky-800 border border-sky-300"
                  title="Relative dates in the article ('this Thursday') were resolved against this date."
                >
                  dated {entry.reference_date}
                </span>
              )}
              {entry.web_search_requested && (
                <span
                  data-testid={`fact-check-web-searches-${idx}`}
                  className={
                    "text-[10px] font-mono-display px-1.5 py-0.5 border " +
                    (entry.web_searches > 0
                      ? "bg-violet-50 text-violet-800 border-violet-300"
                      : "bg-neutral-100 text-neutral-700 border-neutral-300")
                  }
                  title={
                    entry.web_searches > 0
                      ? "Live web searches performed during this run."
                      : "Web search was enabled, but the checker didn't need it for this article."
                  }
                >
                  {entry.web_searches || 0} web{" "}
                  {entry.web_searches === 1 ? "search" : "searches"}
                </span>
              )}
              {checkedLabel(entry) && (
                <span
                  data-testid={`fact-check-checked-${idx}`}
                  className="text-[10px] font-mono-display text-[var(--ink-muted)]"
                  title="Texts this run reviewed"
                >
                  checked: {checkedLabel(entry)}
                </span>
              )}
              {(() => {
                // Scraped URLs and editor material (notes / attached
                // documents) are counted separately — editor material
                // is never "scraped" (2026-09-25).
                const summary = sourcesSummary(entry);
                if (!summary) return null;
                const tip = entry.sources_scraped
                  .map(
                    (s) =>
                      `${(s.status || "").toUpperCase()} · ${s.chars || 0} chars · ${s.label || s.url}`,
                  )
                  .join("\n");
                return (
                  <>
                    {summary.editor && (
                      <span
                        data-testid={`fact-check-editor-sources-${idx}`}
                        className="text-[10px] font-mono-display px-1.5 py-0.5 border bg-neutral-100 text-neutral-700 border-neutral-300"
                        title={tip}
                      >
                        editor: {summary.editor}
                      </span>
                    )}
                    {summary.total > 0 && (
                      <span
                        data-testid={`fact-check-sources-scraped-${idx}`}
                        className={
                          "text-[10px] font-mono-display px-1.5 py-0.5 border " +
                          (summary.clean
                            ? "bg-neutral-100 text-neutral-700 border-neutral-300"
                            : "bg-orange-100 text-orange-800 border-orange-300")
                        }
                        title={tip}
                      >
                        {summary.ok}/{summary.total} sources scraped
                      </span>
                    )}
                  </>
                );
              })()}
              {missingDocsLabel(entry) && (
                <span
                  data-testid={`fact-check-docs-missing-${idx}`}
                  className="text-[10px] font-mono-display px-1.5 py-0.5 border bg-orange-100 text-orange-800 border-orange-300"
                >
                  {missingDocsLabel(entry)}
                </span>
              )}
            </div>
          </div>
          <div className="p-4 space-y-2">
            {(!entry.flagged_claims || entry.flagged_claims.length === 0) ? (
              <div
                data-testid={`fact-check-clean-${idx}`}
                className="text-sm text-emerald-700 flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                No claims flagged — the article checks out.
              </div>
            ) : (
              <>
                <div className="overline mb-1">
                  Flagged claims · {entry.flagged_claims.length}
                </div>
                <ul className="space-y-2">
                  {entry.flagged_claims.map((c, i) => (
                    <li
                      key={i}
                      data-testid={`fact-check-claim-${idx}-${i}`}
                      className="border border-[var(--border)] bg-[var(--bg)] p-3 space-y-1.5"
                    >
                      <div className="flex items-start gap-2 flex-wrap">
                        <span
                          className={
                            "text-[10px] font-mono-display uppercase px-1.5 py-0.5 border " +
                            verdictColor(c.verdict)
                          }
                        >
                          {c.verdict || "unsupported"}
                        </span>
                        {BASIS_LABEL[c.basis] && (
                          <span
                            data-testid={`fact-check-claim-basis-${idx}-${i}`}
                            className="text-[10px] font-mono-display px-1.5 py-0.5 border bg-white text-[var(--ink-muted)] border-[var(--border)]"
                          >
                            {BASIS_LABEL[c.basis]}
                          </span>
                        )}
                        {locationBadge(c) && (
                          <span
                            data-testid={`fact-check-claim-location-${idx}-${i}`}
                            className="text-[10px] font-mono-display px-1.5 py-0.5 border bg-sky-50 text-sky-800 border-sky-300"
                          >
                            {locationBadge(c)}
                          </span>
                        )}
                        <p className="text-sm flex-1 min-w-[200px] leading-snug">
                          {c.claim || "(no claim text)"}
                        </p>
                      </div>
                      {c.comment && (
                        <p className="text-[13px] text-[var(--ink-muted)] leading-snug pl-1 border-l-2 border-[var(--border)] ml-1">
                          {c.comment}
                        </p>
                      )}
                      {(() => {
                        // Editor notes / attached documents show as a
                        // label, not a link (editor://… isn't a URL).
                        const src = sourceDisplay(c, entry);
                        if (!src.text) return null;
                        if (!src.href) {
                          return (
                            <span className="inline-flex items-center gap-1 text-[11px] text-[var(--ink-muted)] break-all">
                              <FileText className="w-3 h-3 shrink-0" />
                              {src.text}
                            </span>
                          );
                        }
                        return (
                          <a
                            href={src.href}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] text-[var(--accent)] hover:underline break-all"
                          >
                            <ExternalLink className="w-3 h-3 shrink-0" />
                            {src.text}
                          </a>
                        );
                      })()}
                      {(() => {
                        // Show in article / Show in post.
                        const key = `${entry.id || idx}:${i}`;
                        const loc = c.location || "article";
                        // Headline and social claims show their text in a
                        // small preview box; body claims highlight in place.
                        const isSocial = loc !== "article";
                        const active = isSocial ? previewKey === key : shownKey === key;
                        const postText =
                          loc === "facebook"
                            ? article.facebook_post
                            : loc === "instagram"
                              ? article.instagram_post
                              : loc === "headline"
                                ? article.headline
                                : "";
                        const parts = isSocial && active
                          ? splitForHighlight(postText, claimCandidates(c))
                          : null;
                        return (
                          <>
                            <button
                              type="button"
                              data-testid={`fact-check-claim-show-${idx}-${i}`}
                              aria-pressed={active}
                              onClick={() => toggleShow(key, c)}
                              title={
                                isSocial
                                  ? "Show the post with this claim highlighted"
                                  : active
                                    ? "Clear the highlight (Esc)"
                                    : "Highlight this claim in the article and scroll to it"
                              }
                              className={
                                "inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 border " +
                                (active
                                  ? "border-amber-300 bg-amber-100 text-amber-800"
                                  : "border-[var(--border)] bg-white text-[var(--ink-muted)] hover:border-[var(--ink)] hover:text-[var(--ink)]")
                              }
                            >
                              <Highlighter className="w-3 h-3" />
                              {isSocial
                                ? active
                                  ? loc === "headline" ? "Hide headline" : "Hide post"
                                  : loc === "headline"
                                    ? "Show in headline"
                                    : `Show in ${loc === "facebook" ? "Facebook" : "Instagram"} post`
                                : active
                                  ? "Clear highlight"
                                  : "Show in article"}
                            </button>
                            {isSocial && active && (
                              <div
                                data-testid={`fact-check-claim-post-${idx}-${i}`}
                                className="mt-1 border border-[var(--border)] bg-white px-2.5 py-2 text-[13px] leading-snug whitespace-pre-wrap break-words"
                              >
                                {parts
                                  ? parts.map((p, k) =>
                                      p.hit ? (
                                        <mark
                                          key={k}
                                          className="bg-yellow-200 text-stone-900 px-0.5"
                                        >
                                          {p.text}
                                        </mark>
                                      ) : (
                                        <span key={k}>{p.text}</span>
                                      ),
                                    )
                                  : postText || "(This post is empty.)"}
                                {!parts && postText && (
                                  <span className="block mt-1.5 text-[11px] text-[var(--ink-muted)] whitespace-normal">
                                    This exact wording isn&apos;t in the {loc === "headline" ? "headline" : "post"} — it may have been edited since the check.
                                  </span>
                                )}
                              </div>
                            )}
                          </>
                        );
                      })()}
                      {(() => {
                        // Proposed change: claim → proposed text → Accept.
                        const fs = fixState(c);
                        if (!fs) return null;
                        const key = `${entry.id}:${i}`;
                        const busy = applyingKey === key;
                        return (
                          <div
                            data-testid={`fact-check-fix-${idx}-${i}`}
                            data-state={fs}
                            className="mt-1 border border-[var(--border)] bg-white"
                          >
                            <div className="px-2.5 pt-2 overline text-[var(--ink-muted)]">
                              Proposed change
                            </div>
                            <div className="px-2.5 py-2 space-y-1.5 text-[13px] leading-snug">
                              <p
                                data-testid={`fact-check-fix-original-${idx}-${i}`}
                                className="text-[var(--ink-muted)] line-through decoration-red-400"
                              >
                                {c.original}
                              </p>
                              <p
                                data-testid={`fact-check-fix-proposed-${idx}-${i}`}
                                className="text-[var(--ink)]"
                              >
                                {c.proposed}
                              </p>
                            </div>
                            {fs === "manual" && (
                              <p className="px-2.5 pb-2 text-[11px] text-amber-800">
                                Couldn't place this automatically — the passage
                                wasn't found exactly once (it may have been
                                edited since this check). Edit it by hand.
                              </p>
                            )}
                            {fs === "accept" && (
                              <div className="px-2.5 pb-2.5">
                                <button
                                  type="button"
                                  data-testid={`fact-check-fix-accept-${idx}-${i}`}
                                  onClick={() =>
                                    applyMut.mutate({
                                      checkId: entry.id,
                                      index: i,
                                      claim: c,
                                    })
                                  }
                                  disabled={
                                    isEditing ||
                                    applyMut.isPending ||
                                    undoMut.isPending ||
                                    !entry.id
                                  }
                                  title={
                                    isEditing
                                      ? "Save or cancel your edits first"
                                      : "Replace the passage with the proposed text. You can undo it afterwards."
                                  }
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {busy ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Check className="w-3 h-3" />
                                  )}
                                  Accept change
                                </button>
                              </div>
                            )}
                            {fs === "applied" && (
                              <div className="px-2.5 pb-2.5 flex items-center gap-2 flex-wrap">
                                <span
                                  data-testid={`fact-check-fix-applied-${idx}-${i}`}
                                  className="inline-flex items-center gap-1 text-[10px] font-mono-display px-1.5 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300"
                                >
                                  <Check className="w-3 h-3" />
                                  Applied
                                </span>
                                <button
                                  type="button"
                                  data-testid={`fact-check-fix-undo-${idx}-${i}`}
                                  onClick={() =>
                                    undoMut.mutate({
                                      checkId: entry.id,
                                      index: i,
                                      claim: c,
                                    })
                                  }
                                  disabled={
                                    isEditing ||
                                    undoMut.isPending ||
                                    applyMut.isPending ||
                                    !entry.id
                                  }
                                  title={
                                    isEditing
                                      ? "Save or cancel your edits first"
                                      : "Put the original wording back. Only the words this fix changed are touched."
                                  }
                                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs border border-[var(--border)] text-[var(--ink-muted)] hover:text-[var(--ink)] hover:border-[var(--ink)] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {busy ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Undo2 className="w-3 h-3" />
                                  )}
                                  Undo
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function PublishingCard({ article, settings, catPickerOpen, setCatPickerOpen }) {
  const qc = useQueryClient();
  const wpCategories = settings?.wordpress_categories || [];
  const wpAuthors = settings?.wordpress_authors || [];
  const [catFilter, setCatFilter] = useState("");

  const selectedCategoryIds = new Set(article.wordpress_categories || []);
  const selectedCategories = wpCategories.filter((c) =>
    selectedCategoryIds.has(c.id)
  );
  const currentAuthor = wpAuthors.find((a) => a.id === article.wordpress_author_id);

  const patchMutation = useMutation({
    mutationFn: (patch) => api.updateArticle(article.id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["article", article.id] });
    },
    onError: () => toast.error("Update failed"),
  });

  const toggleCategory = (catId) => {
    const next = new Set(selectedCategoryIds);
    next.has(catId) ? next.delete(catId) : next.add(catId);
    patchMutation.mutate({ wordpress_categories: Array.from(next) });
  };

  const setAuthor = (authorId) => {
    patchMutation.mutate({
      wordpress_author_id: authorId ? parseInt(authorId, 10) : null,
    });
  };

  const filteredCats = wpCategories.filter((c) =>
    (c.name || "").toLowerCase().includes(catFilter.trim().toLowerCase())
  );

  const noWpData = wpCategories.length === 0 && wpAuthors.length === 0;

  return (
    <div className="mb-10 bg-white border border-[var(--border)]">
      <div className="px-5 py-3 border-b border-[var(--border)] flex items-center gap-2">
        <Send className="w-3.5 h-3.5 text-[var(--accent)]" />
        <span className="overline">WordPress publish settings</span>
      </div>

      {noWpData && (
        <div className="px-5 py-4 text-sm text-[var(--ink-muted)]">
          No WordPress metadata loaded yet.{" "}
          <Link to="/settings" className="text-[var(--accent)] underline">
            Go to Settings → Sync from WordPress
          </Link>{" "}
          to pull categories and authors.
        </div>
      )}

      {!noWpData && (
        <div className="px-5 py-4 space-y-4">
          {/* Author */}
          <div>
            <label className="overline flex items-center gap-1.5 mb-1.5">
              <User className="w-3 h-3" /> WordPress author
            </label>
            <select
              data-testid={CATEGORY_PICKER.authorSelect}
              value={article.wordpress_author_id ?? ""}
              onChange={(e) => setAuthor(e.target.value)}
              disabled={patchMutation.isPending}
              className="w-full px-3 py-2 border border-[var(--border)] bg-white text-sm outline-none focus:border-[var(--slate-900)]"
            >
              <option value="">— No author (uses API caller) —</option>
              {wpAuthors.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-[var(--ink-muted)] mt-1">
              Pre-filled from the writer profile ({article.writer_name}). The
              WordPress user must have publish permissions.
            </p>
          </div>

          {/* Categories */}
          <div>
            <label className="overline flex items-center gap-1.5 mb-1.5">
              <Tag className="w-3 h-3" /> Categories · {selectedCategories.length}{" "}
              selected
            </label>

            {selectedCategories.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {selectedCategories.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => toggleCategory(c.id)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs border border-[var(--accent)] bg-orange-50 text-[var(--accent-hover)] hover:bg-orange-100"
                    title="Click to remove"
                  >
                    {c.name} <X className="w-2.5 h-2.5" />
                  </button>
                ))}
              </div>
            )}

            <button
              data-testid={CATEGORY_PICKER.toggle}
              onClick={() => setCatPickerOpen(!catPickerOpen)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:bg-[var(--secondary)]"
            >
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${catPickerOpen ? "" : "-rotate-90"}`}
              />
              {catPickerOpen ? "Hide categories" : "Add categories"}
            </button>

            {catPickerOpen && (
              <div className="mt-3 border border-[var(--border)] bg-[var(--bg)] p-2">
                <input
                  value={catFilter}
                  onChange={(e) => setCatFilter(e.target.value)}
                  placeholder="Filter categories…"
                  className="w-full px-2 py-1.5 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--slate-900)] mb-2"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1 max-h-64 overflow-auto">
                  {filteredCats.map((c) => {
                    const active = selectedCategoryIds.has(c.id);
                    return (
                      <button
                        key={c.id}
                        data-testid={`${CATEGORY_PICKER.option}-${c.id}`}
                        onClick={() => toggleCategory(c.id)}
                        className={`text-left text-xs px-2 py-1 border flex items-center justify-between gap-2 ${
                          active
                            ? "border-[var(--accent)] bg-orange-50"
                            : "border-[var(--border)] bg-white hover:border-[var(--ink)]"
                        }`}
                      >
                        <span className="truncate" title={c.name}>
                          {active ? "✓ " : ""}
                          {c.name}
                        </span>
                        <span className="text-[10px] text-[var(--ink-muted)] font-mono-display shrink-0">
                          {c.count}
                        </span>
                      </button>
                    );
                  })}
                  {filteredCats.length === 0 && (
                    <div className="col-span-full text-xs text-[var(--ink-muted)] italic p-3 text-center">
                      No categories match &ldquo;{catFilter}&rdquo;
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function ArticleImages({ article, settings }) {
  const qc = useQueryClient();
  const fileInputRef = useRef(null);
  const [subject, setSubject] = useState("");
  const [style, setStyle] = useState("");
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptLoading, setPromptLoading] = useState(false);
  const [showStyle, setShowStyle] = useState(false);
  // Horizontalize tool (landscape canvas + blurred fill) for the image.
  const [hzOpen, setHzOpen] = useState(false);
  // Displayed width of the featured image, so the legend overlay is
  // sized as the same share of the width the publish bake uses
  // (Settings → Images · Disclosure legend style).
  const featuredImgRef = useRef(null);
  const [featuredImgW, setFeaturedImgW] = useState(0);
  useEffect(() => {
    const el = featuredImgRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 0;
      setFeaturedImgW(Math.round(w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [article.generated_image_url]);

  const sourceImages = article.source_images || [];
  const generatedUrl = article.generated_image_url
    ? `${BACKEND_BASE}${article.generated_image_url}`
    : null;
  const isCustomUpload = !!(
    article.generated_image_url &&
    !article.generated_image_prompt
  );

  // When the editor clicks Generate/Regenerate, fetch the split prompt
  // (variable subject + settings-controlled style) so they can review it
  // before we send it to the LLM.
  const openPromptPanel = async () => {
    if (promptOpen) {
      setPromptOpen(false);
      return;
    }
    setPromptOpen(true);
    setPromptLoading(true);
    try {
      const data = await api.getArticleImagePrompt(article.id);
      setSubject(data.subject || "");
      setStyle(data.style || "");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not load prompt template"
      );
      setPromptOpen(false);
    } finally {
      setPromptLoading(false);
    }
  };

  const generateMutation = useMutation({
    mutationFn: () =>
      api.generateArticleImage(article.id, {
        subject: subject.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success("AI image generated");
      qc.invalidateQueries({ queryKey: ["article", article.id] });
      setPromptOpen(false);
    },
    onError: (e) =>
      toast.error(
        e?.response?.data?.detail || e?.message || "Image generation failed"
      ),
  });

  const importMutation = useMutation({
    mutationFn: (url) => api.importSourceImage(article.id, url),
    onSuccess: () => {
      toast.success("Source image saved as featured image");
      qc.invalidateQueries({ queryKey: ["article", article.id] });
    },
    onError: (e) =>
      toast.error(
        e?.response?.data?.detail || e?.message || "Import failed"
      ),
  });

  // "Add this photo to Newsdesk Stock" — ticked before an upload/paste,
  // it also sends the full-size file to the library as pending.
  const addToStock = useAddToStock();
  // "Save to Stock" on each source-image tile (+ "In Stock" marker).
  const srcStock = useSourceStock(article);
  // "Add to Stock" on the featured image already on the article.
  const artStock = useArticleStock(article);

  const uploadMutation = useMutation({
    mutationFn: (file) => api.uploadArticleImage(article.id, file),
    onSuccess: (updated, file) => {
      toast.success("Image uploaded, resized to 1200px and optimised to WebP");
      qc.invalidateQueries({ queryKey: ["article", article.id] });
      addToStock.stashIfEnabled({
        file,
        article: updated,
        imageUrl: updated?.generated_image_url,
        kind: "featured",
      });
    },
    onError: (e) =>
      toast.error(
        e?.response?.data?.detail || e?.message || "Upload failed"
      ),
  });

  const onFileChosen = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPG, PNG, WebP, etc.)");
      e.target.value = "";
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Image is larger than 25 MB");
      e.target.value = "";
      return;
    }
    uploadMutation.mutate(file);
    e.target.value = "";
  };

  const patchMutation = useMutation({
    mutationFn: (patch) => api.updateArticle(article.id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["article", article.id] }),
    onError: () => toast.error("Update failed"),
  });

  const toggleFeatured = () => {
    patchMutation.mutate({
      use_generated_as_featured: !article.use_generated_as_featured,
    });
  };

  // Disclosure legend overlay — surfaces on the image so readers know
  // whether the picture is AI-generated or a stock photo. Persisted on
  // the article doc so refreshes and re-visits keep the overlay.
  const legendLabels = {
    ai_generated: "AI-generated illustration",
    stock_photo: "Stock photo, for illustrative purposes",
  };
  const currentLegend = article.image_legend || "";
  const legendEnabled = !!currentLegend;
  // Source names offered as courtesy suggestions. Cheap enough to
  // recompute per render (max ~10 sources), so no useMemo needed.
  const courtesyNames = sourceNameSuggestions(article, settings?.sources);
  // The resolved caption text — same logic as backend `resolve_legend_text`.
  // Note "custom" and "courtesy" share `image_legend_custom_text`: the
  // former bakes it verbatim, the latter prefixes it. Text typed under
  // one key is deliberately kept when switching to the other so an
  // accidental radio click never destroys the editor's typing.
  const resolvedLegend = (() => {
    if (!currentLegend) return "";
    const text = (article.image_legend_custom_text || "").trim();
    if (currentLegend === "custom") return text;
    if (currentLegend === "courtesy") {
      return text ? `${COURTESY_PREFIX}${text}` : "";
    }
    return legendLabels[currentLegend] || "";
  })();
  const toggleLegendEnabled = () => {
    // Turning it on defaults to "ai_generated" — the far more common
    // case for this app (Gemini-generated hero images). Turning it off
    // clears both the key and any custom text.
    patchMutation.mutate(
      legendEnabled
        ? { image_legend: "", image_legend_custom_text: "" }
        : { image_legend: "ai_generated" },
    );
  };
  const setLegendValue = (value) => {
    // Selecting "Photo courtesy of…" with no name yet pre-fills the
    // first source the writer used, so the common case is one click.
    // Sent as a single PATCH — `ArticleUpdate` accepts both fields and
    // the route uses exclude_none, so this is one round trip.
    if (
      value === "courtesy" &&
      !(article.image_legend_custom_text || "").trim() &&
      courtesyNames.length > 0
    ) {
      patchMutation.mutate({
        image_legend: value,
        image_legend_custom_text: courtesyNames[0],
      });
      return;
    }
    patchMutation.mutate({ image_legend: value });
  };
  // Local buffer for the custom text so typing doesn't fire a PATCH on
  // every keystroke. Committed on blur / Enter.
  const [customDraft, setCustomDraft] = useState(
    article.image_legend_custom_text || "",
  );
  useEffect(() => {
    setCustomDraft(article.image_legend_custom_text || "");
  }, [article.image_legend_custom_text]);
  const commitCustomText = () => {
    const trimmed = customDraft.trim();
    if (trimmed === (article.image_legend_custom_text || "")) return;
    patchMutation.mutate({ image_legend_custom_text: trimmed });
  };

  return (
    <div className="mb-10 bg-white border border-[var(--border)]">
      <div className="px-4 sm:px-5 py-3 border-b border-[var(--border)] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2">
          <Camera className="w-3.5 h-3.5 text-[var(--accent)]" />
          <span className="overline">Article images</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            ref={fileInputRef}
            data-testid={IMAGES.uploadInput || "upload-image-input"}
            type="file"
            accept="image/*"
            onChange={onFileChosen}
            className="hidden"
          />
          <button
            data-testid="upload-image-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
            title="Upload your own picture — it will be resized to 1200px wide and optimised to WebP"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-60"
          >
            {uploadMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Upload className="w-3.5 h-3.5" />
            )}
            Upload
          </button>
          {/* Clipboard paste — same destination as the Upload button.
              `hotkey` also arms a page-wide Cmd/Ctrl+V (ignored inside
              text fields and the body editor); the add-body-image
              modal stacks on top of it while it is open. */}
          <PasteImageButton
            onImage={(file) => uploadMutation.mutate(file)}
            busy={uploadMutation.isPending}
            testId="paste-featured-image-btn"
            title="Paste an image from the clipboard as the featured image — or press Cmd/Ctrl+V anywhere on this page"
          />
          <button
            data-testid={IMAGES.generateButton}
            onClick={openPromptPanel}
            disabled={generateMutation.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--slate-900)] text-white hover:bg-black disabled:opacity-60"
          >
            {generateMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Sparkles className="w-3.5 h-3.5" />
            )}
            {generatedUrl ? "Regenerate with AI" : "Generate with AI"}
          </button>
        </div>
      </div>
      {addToStock.configured && (
        <div className="px-4 sm:px-5 py-2 border-b border-[var(--border)]">
          <AddToStockOption stock={addToStock} testIdPrefix="featured" />
        </div>
      )}

      <div className="p-4 sm:p-5 space-y-5">
        {/* Source images */}
        <div>
          <div className="overline mb-2">
            From source articles · {sourceImages.length}
          </div>
          {sourceImages.length === 0 ? (
            <div className="text-xs text-[var(--ink-muted)] italic">
              No source images were collected. The AI can still generate an
              image without references.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {sourceImages.map((u, i) => (
                <div
                  key={i}
                  className="relative group aspect-video bg-[var(--secondary)] overflow-hidden border border-[var(--border)] hover:border-[var(--ink)]"
                >
                  <a
                    href={u}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`${IMAGES.sourceImage}-${i}`}
                    className="block w-full h-full"
                  >
                    <img
                      src={u}
                      alt={`Source ${i + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </a>
                  <SourceStockButton
                    srcStock={srcStock}
                    url={u}
                    defaultCredit={courtesyNames[0] ? `${COURTESY_PREFIX}${courtesyNames[0]}` : ""}
                    className={
                      "absolute top-1 right-1 " +
                      (srcStock.savedMap[u] ? "" : "opacity-0 group-hover:opacity-100")
                    }
                  />
                  {/* Hover-in "Use as featured" pill. Server-side fetch
                      + object-storage copy so cross-origin doesn't
                      matter and the source can be a press-room CDN. */}
                  <button
                    data-testid={`source-image-use-${i}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      importMutation.mutate(u);
                    }}
                    disabled={importMutation.isPending}
                    title="Save this photo as the featured image (no download needed)"
                    className="absolute inset-x-1 bottom-1 text-[10px] px-2 py-1 bg-white/95 border border-[var(--border)] shadow-sm opacity-0 group-hover:opacity-100 hover:bg-white disabled:opacity-40 flex items-center justify-center gap-1"
                  >
                    {importMutation.isPending && importMutation.variables === u ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" /> Saving…
                      </>
                    ) : (
                      "Use as featured"
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Prompt panel — split into editor-tuned subject + settings-controlled style */}
        {promptOpen && (
          <div className="border border-[var(--border)] bg-[var(--bg)] p-3 space-y-3">
            {promptLoading ? (
              <div className="text-sm text-[var(--ink-muted)] flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Loading prompt template…
              </div>
            ) : (
              <>
                <div>
                  <div className="overline mb-1.5">
                    Image subject — variable part
                  </div>
                  <p className="text-[11px] text-[var(--ink-muted)] mb-1.5">
                    The article-specific portion. Fine-tune the scene,
                    subject or angle you want the AI to render.
                  </p>
                  <textarea
                    data-testid={IMAGES.promptInput}
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    rows={4}
                    placeholder="Editorial photograph for a news article titled…"
                    className="w-full border border-[var(--border)] px-3 py-2 text-sm outline-none focus:border-[var(--slate-900)] bg-white font-mono-display"
                  />
                </div>

                <div>
                  <button
                    type="button"
                    data-testid="toggle-image-style-preview"
                    onClick={() => setShowStyle(!showStyle)}
                    className="text-[11px] uppercase tracking-wider text-[var(--ink-muted)] font-mono-display hover:text-[var(--ink)]"
                  >
                    {showStyle ? "▾" : "▸"} Style suffix (from Settings)
                  </button>
                  {showStyle && (
                    <>
                      <p className="text-[11px] text-[var(--ink-muted)] mt-1 mb-1.5">
                        This is appended to every image prompt for the current
                        property.{" "}
                        <a
                          href="/settings"
                          className="text-[var(--accent)] underline"
                        >
                          Edit in Settings
                        </a>
                        .
                      </p>
                      <pre className="whitespace-pre-wrap font-mono-display text-[11px] bg-white border border-[var(--border)] p-2 text-[var(--ink-muted)]">
                        {style}
                      </pre>
                    </>
                  )}
                </div>
              </>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPromptOpen(false)}
                className="px-3 py-1.5 text-sm border border-[var(--border)] hover:bg-[var(--secondary)]"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-image-generate"
                data-ai="true"
                onClick={() => generateMutation.mutate()}
                disabled={
                  generateMutation.isPending ||
                  promptLoading ||
                  !subject.trim()
                }
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-60"
              >
                {generateMutation.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5" />
                )}
                Generate image
              </button>
            </div>
          </div>
        )}

        {/* Generated image */}
        {generatedUrl && (
          <div>
            <div className="overline mb-2">
              {isCustomUpload ? "Custom uploaded image" : "AI-generated image"}
            </div>
            <div className="border border-[var(--border)] bg-black inline-block max-w-full relative">
              <img
                ref={featuredImgRef}
                data-testid={IMAGES.generatedImage}
                src={generatedUrl}
                alt="AI-generated"
                className="max-w-full h-auto block"
              />
              {legendEnabled && resolvedLegend && featuredImgW > 0 && (
                <div
                  data-testid="image-legend-overlay"
                  style={legendOverlayStyle(settings?.image_legend_style_effective, featuredImgW)}
                >
                  {resolvedLegend}
                </div>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
              <button
                type="button"
                data-testid="horizontalize-open"
                onClick={() => setHzOpen(true)}
                title="Make a landscape version: the photo on a blurred copy of itself"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)]"
              >
                <ImageIcon className="w-3.5 h-3.5" />
                {horizontalizeIsActive(article) ? "Adjust horizontal version" : "Horizontalize"}
              </button>
              <ArticleStockButton
                artStock={artStock}
                url={article.generated_image_url}
                previewSrc={generatedUrl}
                defaultCredit={
                  currentLegend === "custom" || currentLegend === "courtesy" ? resolvedLegend : ""
                }
              />
              <span className="text-[11px] text-[var(--ink-muted)]">
                {horizontalizeIsActive(article)
                  ? "This is a horizontalized image. Reopen to move the photo or restore the original."
                  : "For vertical or square photos: a landscape image with the photo on a blurred background."}
              </span>
            </div>
            {hzOpen && (
              <HorizontalizeDialog
                article={article}
                onClose={() => setHzOpen(false)}
                onSaved={() => qc.invalidateQueries({ queryKey: ["article", article.id] })}
              />
            )}
            <div className="mt-3 space-y-2">
              <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  data-testid={IMAGES.useAsFeaturedToggle}
                  checked={!!article.use_generated_as_featured}
                  onChange={toggleFeatured}
                  disabled={patchMutation.isPending}
                  className="w-4 h-4 accent-[var(--accent)]"
                />
                <span>
                  Use as{" "}
                  <span className="font-semibold">WordPress featured image</span>
                </span>
                {article.wordpress_featured_media_id && (
                  <span className="text-[10px] text-emerald-700 font-mono-display">
                    (uploaded · media {article.wordpress_featured_media_id})
                  </span>
                )}
              </label>
              {article.use_generated_as_featured && (
                <p className="text-[11px] text-[var(--ink-muted)]">
                  On publish, this image will be uploaded to WordPress and set
                  as the featured image for this post.
                </p>
              )}

              {/* Disclosure legend overlay toggle */}
              <div className="pt-2 border-t border-[var(--border)]/60">
                <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    data-testid="image-legend-toggle"
                    checked={legendEnabled}
                    onChange={toggleLegendEnabled}
                    disabled={patchMutation.isPending}
                    className="w-4 h-4 accent-[var(--accent)]"
                  />
                  <span>
                    Add <span className="font-semibold">disclosure legend</span>{" "}
                    to the image
                  </span>
                </label>
                {legendEnabled && (
                  <div className="mt-2 pl-6 space-y-1">
                    {Object.entries(legendLabels).map(([key, label]) => (
                      <label
                        key={key}
                        className="flex items-center gap-2 cursor-pointer text-sm"
                      >
                        <input
                          type="radio"
                          name={`legend-${article.id}`}
                          data-testid={`image-legend-radio-${key}`}
                          value={key}
                          checked={currentLegend === key}
                          onChange={() => setLegendValue(key)}
                          disabled={patchMutation.isPending}
                          className="w-3.5 h-3.5 accent-[var(--accent)]"
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="radio"
                        name={`legend-${article.id}`}
                        data-testid="image-legend-radio-courtesy"
                        value="courtesy"
                        checked={currentLegend === "courtesy"}
                        onChange={() => setLegendValue("courtesy")}
                        disabled={patchMutation.isPending}
                        className="w-3.5 h-3.5 accent-[var(--accent)]"
                      />
                      <span>Photo courtesy of…</span>
                    </label>
                    {currentLegend === "courtesy" && (
                      <div className="ml-6 w-full max-w-md">
                        <input
                          type="text"
                          data-testid="image-legend-courtesy-input"
                          list={`legend-courtesy-sources-${article.id}`}
                          value={customDraft}
                          onChange={(e) => setCustomDraft(e.target.value)}
                          onBlur={commitCustomText}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.currentTarget.blur();
                            }
                          }}
                          placeholder="e.g., Ayuntamiento de Los Cabos"
                          maxLength={120}
                          className="w-full px-2 py-1 text-sm border border-[var(--border)] focus:border-[var(--ink)] outline-none"
                        />
                        <datalist id={`legend-courtesy-sources-${article.id}`}>
                          {courtesyNames.map((n) => (
                            <option key={n} value={n} />
                          ))}
                        </datalist>
                        <p className="text-[11px] text-[var(--ink-muted)] pt-1">
                          Will read:{" "}
                          <span className="font-semibold">
                            {COURTESY_PREFIX}
                            {customDraft.trim() || "…"}
                          </span>
                        </p>
                      </div>
                    )}
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="radio"
                        name={`legend-${article.id}`}
                        data-testid="image-legend-radio-custom"
                        value="custom"
                        checked={currentLegend === "custom"}
                        onChange={() => setLegendValue("custom")}
                        disabled={patchMutation.isPending}
                        className="w-3.5 h-3.5 accent-[var(--accent)]"
                      />
                      <span>Other…</span>
                    </label>
                    {currentLegend === "custom" && (
                      <input
                        type="text"
                        data-testid="image-legend-custom-input"
                        value={customDraft}
                        onChange={(e) => setCustomDraft(e.target.value)}
                        onBlur={commitCustomText}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            e.currentTarget.blur();
                          }
                        }}
                        placeholder="e.g., Photo by Juan Perez / Gringo Gazette"
                        maxLength={120}
                        className="ml-6 w-full max-w-md px-2 py-1 text-sm border border-[var(--border)] focus:border-[var(--ink)] outline-none"
                      />
                    )}
                    <p className="text-[11px] text-[var(--ink-muted)] pt-1">
                      Shows as a small caption overlay at the bottom-left of
                      the image (both in the editor and baked into the
                      WordPress featured image and social story cards).
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

